/**
 * FRONT-1996 "Take the wheel" — the "Astra needs your help" notification's
 * own deep link (built server-side by `operatorRunChatAction`,
 * `src/operator/loop.ts`, as `/chat?operatorRun=<runId>`) needs a reader on
 * the frontend to actually open the operator panel for that run.
 * `operatorRun` is the ONE literal parameter name both sides must agree on.
 * This file's own test pins only the FRONTEND's half of that agreement
 * (this exact string) — there is no shared import with the backend, so a
 * rename on the BACKEND side is caught only by the backend's own test
 * pinning `operatorRunChatAction`'s href (`src/operator/loop.test.ts`), not
 * by anything here. If either side's literal ever drifts from the other,
 * the link silently lands on a chat page with nothing open and neither
 * test goes red for it.
 *
 * Pure on purpose: `operator-panel-host.tsx` (mounted once, on every route)
 * owns the `useSearchParams()` React wiring and the actual
 * `openOperatorPanel` call; this file only decides WHAT the URL says and
 * WHAT the URL should look like afterwards, so both are unit-testable
 * without a Router in the tree.
 */

import type { FeatureFlagStatus } from '~/hooks/queries/use-feature-flags';

export const OPERATOR_RUN_DEEP_LINK_PARAM = 'operatorRun';

/**
 * The run id to open, or `null` when the URL carries no (non-empty)
 * `operatorRun` param — never distinguishes "absent" from "empty string",
 * since neither is a run worth opening a panel for.
 */
export function readOperatorRunDeepLink(params: URLSearchParams): string | null {
  const runId = params.get(OPERATOR_RUN_DEEP_LINK_PARAM);
  return runId && runId.trim().length > 0 ? runId : null;
}

/**
 * A NEW `URLSearchParams` with the deep-link param removed and every other
 * param preserved untouched (order included) — so the link can't re-open
 * the panel on a refresh or a back-navigation once it's been consumed.
 * Never mutates its input.
 */
export function stripOperatorRunDeepLink(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(OPERATOR_RUN_DEEP_LINK_PARAM);
  return next;
}

/**
 * FRONT-1996 review m13/m14: the whole deep-link decision as one pure,
 * framework-free function — pulled out of `operator-panel-host.tsx`'s
 * `useEffect` so this logic (as opposed to whether the effect still calls
 * it — see that file's own comment on the same limit) is unit-testable
 * without a DOM or a Router: "which runId, open or not, strip or not" all
 * settled here, taking `openPanel`/`setSearchParams` as plain injected
 * functions rather than reading `openOperatorPanel`/`useSearchParams`
 * directly.
 *
 * `status` (FRONT-2278, replacing review m14's plain `enabled: boolean`) is
 * whatever the host already uses to decide the panel is available to this
 * caller at all (`useEddyMachineStatus()`), and it has three states rather
 * than two:
 *  - `'enabled'` — open the panel, then strip the param. A stale or
 *    malicious link must never open a surface this person can't otherwise
 *    reach, so this is the only branch that calls `openPanel`.
 *  - `'disabled'` — never open the panel, but still STRIP the param, so a
 *    dead link doesn't linger in the address bar forever.
 *  - `'loading'` — the flag hasn't answered yet, so we don't know which of
 *    the above we're in. Do NOTHING: neither open nor strip. This is the
 *    FRONT-2278 fix — on a cold navigation the flags query resolves after
 *    this effect's first run, and the old two-state `enabled: boolean`
 *    could only read that as `false`, which stripped the param before the
 *    flag ever came back `true`. Leaving the param in place lets a later
 *    render, once `status` is actually `'enabled'` or `'disabled'`, make
 *    the real decision.
 */
export function handleOperatorRunDeepLink(input: {
  status: FeatureFlagStatus;
  searchParams: URLSearchParams;
  openPanel: (input: { runId: string }) => void;
  setSearchParams: (next: URLSearchParams, opts: { replace: boolean }) => void;
}): void {
  const runId = readOperatorRunDeepLink(input.searchParams);
  if (!runId) return;
  if (input.status === 'loading') return;
  if (input.status === 'enabled') input.openPanel({ runId });
  input.setSearchParams(stripOperatorRunDeepLink(input.searchParams), { replace: true });
}
